import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSetAltPaths } from "../../context/AltPathContext.jsx";
import imageManifest from "../../data/imageManifest.json";

const SITE_NAME = "Tales&Legends";
const SITE_URL = "https://talesandlegends.org";

// Every OG consumer that matters (Facebook, LinkedIn, WhatsApp, Discord,
// Slack) ignores a relative og:image outright, so nothing here may ever
// emit a bare "/assets/..." path.
function absoluteUrl(pathOrUrl) {
  return pathOrUrl?.startsWith("/") ? `${SITE_URL}${pathOrUrl}` : pathOrUrl;
}

// 1200x630 JPEG per language, hand-built from the brand palette (see
// scripts/generate-share-images.js) — the fallback for every page with no
// hero image of its own.
const DEFAULT_SHARE_IMAGE = { de: "/assets/share/default-de.jpg", en: "/assets/share/default-en.jpg" };
const SHARE_IMAGE_WIDTH = 1200;
const SHARE_IMAGE_HEIGHT = 630;
const OG_LOCALE = { de: "de_DE", en: "en_GB" };

/**
 * The page's own hero only becomes the share image when the optimizer has
 * actually produced its 1200x630 `-og.jpg` derivative (§ scripts/
 * optimize-images.js). A frontmatter `image` whose file was never added to
 * public/assets yet has no manifest entry at all — pointing og:image at
 * that path would hand every social platform a 404 and a blank preview
 * card, which is strictly worse than the language default.
 */
function resolveShareImage(image, lang) {
  const entry = image ? imageManifest[image] : undefined;
  return absoluteUrl(entry?.og || DEFAULT_SHARE_IMAGE[lang]);
}

/**
 * Central SEO component per §10.3 — title/description, hreflang alternates,
 * Open Graph. `noindex` is used for untranslated fallback pages (§10.2).
 *
 * `path` is the CURRENT language's path; `altPath` is the sibling
 * language's path. These are NOT the same string with /de//en/ swapped —
 * most static routes have different slugs per language (e.g. /impressum
 * vs /legal-notice), so the caller must supply both explicitly. For
 * language-identical slugs (events, donation projects), pass the same
 * value for both.
 */
export default function SEOHead({
  lang,
  path,
  altPath = path,
  title,
  category,
  description,
  image,
  imageAlt,
  type = "website",
  publishedTime,
  modifiedTime,
  noindex = false,
  hasTranslation = true,
}) {
  const canonical = `${SITE_URL}/${lang}${path}`;
  // §10.3 title pattern for knowledge/event pages: "{title} – {category} |
  // Tales&Legends"; everything else keeps the plain "{title} | Tales &
  // Legends" pattern. Callers with a hand-tuned SEO title (frontmatter
  // `metaTitle`) pass `category={undefined}` so the infix is skipped —
  // `title` in that case is already the exact phrase meant to sit before
  // " | Tales&Legends".
  const fullTitle = category ? `${title} – ${category} | ${SITE_NAME}` : `${title} | ${SITE_NAME}`;
  const shareImage = resolveShareImage(image, lang);
  const dePath = lang === "de" ? path : altPath;
  const enPath = lang === "en" ? path : altPath;

  // The header's language switcher needs the exact same DE/EN sibling
  // paths computed above for hreflang — reusing them here means callers
  // don't need to wire this up separately (see AltPathContext.jsx).
  const setAltPaths = useSetAltPaths();
  useEffect(() => {
    setAltPaths({ dePath, enPath, hasTranslation });
  }, [dePath, enPath, hasTranslation, setAltPaths]);

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />

      {hasTranslation && <link rel="alternate" hrefLang="de" href={`${SITE_URL}/de${dePath}`} />}
      {hasTranslation && <link rel="alternate" hrefLang="en" href={`${SITE_URL}/en${enPath}`} />}
      {hasTranslation && (
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/de${dePath}`} />
      )}

      {(noindex || !hasTranslation) && <meta name="robots" content="noindex, follow" />}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={OG_LOCALE[lang]} />
      {hasTranslation && (
        <meta property="og:locale:alternate" content={OG_LOCALE[lang === "de" ? "en" : "de"]} />
      )}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={shareImage} />
      <meta property="og:image:width" content={String(SHARE_IMAGE_WIDTH)} />
      <meta property="og:image:height" content={String(SHARE_IMAGE_HEIGHT)} />
      <meta property="og:image:alt" content={imageAlt || title} />
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={shareImage} />
      <meta name="twitter:image:alt" content={imageAlt || title} />
    </Helmet>
  );
}
